/* Blog filter form — drawer + instant filtering + infinite scroll */
class BlogFilterForm extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById('BlogGrid');
    this.container = document.getElementById('BlogGridContainer');
    this.drawer = document.getElementById('BlogFilterDrawer');
    this.loading = false;
    this.allLoaded = !this.container.dataset.nextUrl;

    // Drawer open/close
    this.querySelectorAll('[data-open-filters]').forEach(btn =>
      btn.addEventListener('click', () => this.openDrawer())
    );
    this.querySelectorAll('[data-close-filters]').forEach(btn =>
      btn.addEventListener('click', () => this.closeDrawer())
    );
    this.querySelector('[data-clear-filters]')?.addEventListener('click', () => this.clearFilters());
    this.querySelector('[data-apply-filters]')?.addEventListener('click', () => this.applyAndClose());

    // Mobile categories accordion
    this.navToggle = this.querySelector('[data-nav-toggle]');
    this.navLinks = this.querySelector('.blog-nav__links');
    if (this.navToggle && this.navLinks) {
      this.navToggle.addEventListener('click', () => {
        const open = this.navLinks.classList.toggle('is-open');
        this.navToggle.setAttribute('aria-expanded', open);
      });
    }

    // Instant filter on checkbox change
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        // Drawer "All" checkbox — clears category checkboxes
        if (e.target.matches('[data-nav-all-checkbox]')) {
          if (e.target.checked) {
            this.querySelectorAll('input[type="checkbox"][data-group="category"]:not([data-nav-all-checkbox])').forEach(c => { c.checked = false; });
          } else {
            // Prevent unchecking "All" directly when no category selected
            const anyCategory = this.querySelector('input[type="checkbox"][data-group="category"]:not([data-nav-all-checkbox]):checked');
            if (!anyCategory) {
              e.target.checked = true;
              return;
            }
          }
          this.applyFilters();
          this.checkLoadMore();
          return;
        }
        // Enforce single-select for category group
        if (e.target.dataset.group === 'category' && e.target.checked) {
          this.querySelectorAll('input[type="checkbox"][data-group="category"]').forEach(c => {
            if (c !== e.target) c.checked = false;
          });
        }
        this.applyFilters();
        this.checkLoadMore();
      }
    });

    // Nav bar category buttons
    this.querySelectorAll('[data-nav-filter]').forEach(btn => {
      btn.addEventListener('click', () => this.toggleNavFilter(btn));
    });

    // "All" nav button — clears category filters
    this.querySelectorAll('[data-nav-all]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('input[type="checkbox"][data-group="category"]').forEach(c => { c.checked = false; });
        this.applyFilters();
        this.checkLoadMore();
      });
    });

    this.setupInfiniteScroll();
    this.restoreFromUrl();
    this.reflowGrid();
  }

  get checkboxes() {
    return this.querySelectorAll('input[type="checkbox"]');
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    document.body.classList.add('blog-filter-drawer-open');
    this.drawer.setAttribute('aria-hidden', 'false');
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('blog-filter-drawer-open');
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  clearFilters() {
    this.checkboxes.forEach(cb => { cb.checked = false; });
    this.applyFilters();
  }

  toggleNavFilter(btn) {
    const value = btn.dataset.navFilter;
    const cb = this.querySelector(`input[type="checkbox"][data-group="category"][value="${value}"]`);
    if (!cb) return;
    const willCheck = !cb.checked;
    // Deselect all other category checkboxes — only one category at a time
    this.querySelectorAll('input[type="checkbox"][data-group="category"]').forEach(c => { c.checked = false; });
    cb.checked = willCheck;
    this.applyFilters();
    this.checkLoadMore();
  }

  syncNavActive() {
    const activeCategories = [];
    this.checkboxes.forEach(cb => {
      if (cb.checked && cb.dataset.group === 'category' && !cb.hasAttribute('data-nav-all-checkbox')) {
        activeCategories.push(cb.value);
      }
    });
    this.querySelectorAll('[data-nav-filter]').forEach(btn => {
      btn.classList.toggle('is-active', activeCategories.includes(btn.dataset.navFilter));
    });
    this.querySelectorAll('[data-nav-all]').forEach(btn => {
      btn.classList.toggle('is-active', activeCategories.length === 0);
    });
    this.querySelectorAll('[data-nav-all-checkbox]').forEach(cb => {
      cb.checked = activeCategories.length === 0;
    });
  }

  applyAndClose() {
    this.applyFilters();
    this.checkLoadMore();
    this.closeDrawer();
  }

  getActiveFilters() {
    const groups = {};
    this.checkboxes.forEach(cb => {
      if (cb.checked && !cb.hasAttribute('data-nav-all-checkbox')) {
        const group = cb.dataset.group;
        if (!groups[group]) groups[group] = [];
        groups[group].push(cb.value);
      }
    });
    return groups;
  }

  hasActiveFilters() {
    return [...this.checkboxes].some(cb => cb.checked && !cb.hasAttribute('data-nav-all-checkbox'));
  }

  applyFilters() {
    const activeFilters = this.getActiveFilters();
    const groups = Object.keys(activeFilters);
    const articles = this.grid.querySelectorAll('.blog-filtered__article');

    articles.forEach(article => {
      if (groups.length === 0) {
        article.style.display = '';
        return;
      }

      const tags = article.dataset.tags.split(',');
      let visible = true;

      for (const group of groups) {
        const groupValues = activeFilters[group];
        if (!groupValues.some(v => tags.includes(v))) {
          visible = false;
          break;
        }
      }

      article.style.display = visible ? '' : 'none';
    });

    this.reflowGrid();
    this.updateUrl(activeFilters);
    this.updateEmptyState();
    this.syncNavActive();
  }

  reflowGrid() {
    const articles = this.grid.querySelectorAll('.blog-filtered__article');
    let visibleIndex = 0;
    articles.forEach(article => {
      article.classList.remove('blog-filtered__hero', 'blog-filtered__hero--reversed', 'blog-filtered__hero--first', 'blog-filtered__card');
      if (article.style.display === 'none') return;
      const mod4 = visibleIndex % 4;
      const mod8 = visibleIndex % 8;
      if (mod4 === 0) {
        article.classList.add('blog-filtered__hero');
        if (visibleIndex === 0) article.classList.add('blog-filtered__hero--first');
        if (mod8 === 4) article.classList.add('blog-filtered__hero--reversed');
      } else {
        article.classList.add('blog-filtered__card');
      }
      visibleIndex++;
    });
  }

  updateUrl(activeFilters) {
    const url = new URL(window.location);
    url.searchParams.delete('filter');
    const groups = Object.keys(activeFilters);
    for (const group of groups) {
      for (const value of activeFilters[group]) {
        url.searchParams.append('filter', group + ':' + value);
      }
    }
    window.history.replaceState(null, '', url);
  }

  restoreFromUrl() {
    const url = new URL(window.location);
    const params = url.searchParams.getAll('filter');
    if (!params.length) return;

    params.forEach(param => {
      const sep = param.indexOf(':');
      if (sep === -1) return;
      const group = param.slice(0, sep);
      const value = param.slice(sep + 1);
      this.checkboxes.forEach(cb => {
        if (cb.dataset.group === group && cb.value === value) {
          cb.checked = true;
        }
      });
    });
    this.applyFilters();
    this.checkLoadMore();
  }

  updateEmptyState() {
    const visibleCount = this.getVisibleCount();
    let emptyMsg = this.container.querySelector('.blog-filtered__empty');

    if (visibleCount === 0 && this.hasActiveFilters() && this.allLoaded) {
      if (!emptyMsg) {
        emptyMsg = document.createElement('p');
        emptyMsg.className = 'blog-filtered__empty';
        emptyMsg.textContent = 'No articles match the selected filters.';
        this.grid.after(emptyMsg);
      }
      emptyMsg.style.display = '';
    } else if (emptyMsg) {
      emptyMsg.style.display = 'none';
    }
  }

  getVisibleCount() {
    return [...this.grid.querySelectorAll('.blog-filtered__article')]
      .filter(a => a.style.display !== 'none').length;
  }

  checkLoadMore() {
    if (this.allLoaded || this.loading) return;

    const visibleCount = this.getVisibleCount();
    if (visibleCount < 6 && this.hasActiveFilters()) {
      this.loadNextPage();
    }
  }

  setupInfiniteScroll() {
    if (this.allLoaded) return;

    const pagination = this.container.querySelector('.pagination-wrapper');
    if (pagination) pagination.style.display = 'none';

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'blog-filtered__sentinel';
    this.grid.after(this.sentinel);

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.loading && !this.allLoaded) {
        this.loadNextPage();
      }
    }, { rootMargin: '0px 0px 400px 0px' });

    this.observer.observe(this.sentinel);
  }

  loadNextPage() {
    const nextUrl = this.container.dataset.nextUrl;
    if (!nextUrl || this.loading) return;

    this.loading = true;

    fetch(nextUrl)
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.getElementById('BlogGrid');

        if (newGrid) {
          const newArticles = newGrid.querySelectorAll('.blog-filtered__article');
          newArticles.forEach(article => {
            this.grid.insertAdjacentHTML('beforeend', article.outerHTML);
          });
        }

        const newContainer = doc.getElementById('BlogGridContainer');
        const newNextUrl = newContainer ? newContainer.dataset.nextUrl : '';

        if (newNextUrl) {
          this.container.dataset.nextUrl = newNextUrl;
        } else {
          this.container.dataset.nextUrl = '';
          this.allLoaded = true;

          if (this.sentinel) {
            this.sentinel.remove();
            this.sentinel = null;
          }
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }

          const pagination = this.container.querySelector('.pagination-wrapper');
          if (pagination) pagination.remove();
        }

        this.loading = false;
        this.applyFilters();
        this.checkLoadMore();
      })
      .catch(e => {
        console.error(e);
        this.loading = false;
      });
  }
}

customElements.define('blog-filter-form', BlogFilterForm);
